import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTheme } from "@mui/material/styles";
import { IoHomeOutline } from "react-icons/io5";
import { MdOutlineBrush, MdOutlineGridView, MdOutlineLocalShipping, MdOutlinePeopleAlt, MdOutlinePrecisionManufacturing, MdOutlineSettings } from "react-icons/md";

interface HCPSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface IMenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface IMenuGroup {
  group: string | null;
  items: IMenuItem[];
}

const MENU: IMenuGroup[] = [
  {
    group: null,
    items: [
      { title: 'Acessar HUB', path: '/dashboard', icon: <IoHomeOutline size={20} /> },
    ],
  },
  {
    group: 'Fluxos',
    items: [
      { title: 'Arte Final',             path: '/hcp/arte-andamento',    icon: <MdOutlineBrush size={20} /> },
      { title: 'Montagem de Cartelas',   path: '/hcp/montagem-cartelas', icon: <MdOutlineGridView size={20} /> },
      { title: 'OS e Produção',          path: '/hcp/os-producao',       icon: <MdOutlinePrecisionManufacturing size={20} />,    disabled: true },
      { title: 'Logística',              path: '/hcp/logistica',         icon: <MdOutlineLocalShipping size={20} />,             disabled: true },
    ],
  },
  {
    group: 'Gestão',
    items: [
      { title: 'Colaboradores',  path: '/hcp/colaboradores',  icon: <MdOutlinePeopleAlt size={20} />, disabled: true },
      { title: 'Configurações',  path: '/hcp/configuracoes',  icon: <MdOutlineSettings size={20} />,  disabled: true },
    ],
  },
];

export default function HCPSidebar({ open, onClose }: HCPSidebarProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    onClose();
  };

  const renderItem = (item: IMenuItem) => {
    const isActive = !item.disabled && location.pathname.startsWith(item.path);

    const button = (
      <ListItemButton
        onClick={() => !item.disabled && navigate(item.path)}
        selected={isActive}
        disabled={item.disabled}
        sx={{
          mx: 1,
          mb: 0.5,
          borderRadius: 1,
          '&.Mui-selected': {
            backgroundColor: 'rgba(132, 168, 2, 0.15)',
            color: theme.palette.primary.dark,
            '&:hover': { backgroundColor: 'rgba(132, 168, 2, 0.25)' },
          },
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: 'rgba(132, 168, 2, 0.08)',
            color: theme.palette.primary.main,
          },
          color: isActive ? theme.palette.primary.dark : theme.palette.text.secondary,
          opacity: item.disabled ? 0.45 : 1,
        }}
      >
        <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500 }}
        />
        {item.disabled && (
          <Typography variant="caption" sx={{ fontSize: '0.60rem', color: 'text.disabled', ml: 0.5 }}>
            em breve
          </Typography>
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.title} disablePadding>
        {item.disabled
          ? <Tooltip title="Em breve" placement="right">{button}</Tooltip>
          : button
        }
      </ListItem>
    );
  };

  const list = () => (
    <Box sx={{ width: 280 }} role="presentation" onClick={toggleDrawer} onKeyDown={toggleDrawer}>
      <Box sx={{ padding: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${theme.palette.divider}`, minHeight: '73px' }}>
        <Typography variant="h6" color="primary" fontWeight="bold">HCP - Produção</Typography>
      </Box>

      {MENU.map((section, i) => (
        <Box key={i}>
          {section.group && (
            <Typography
              variant="caption"
              sx={{ px: 2, pt: 2, pb: 0.5, display: 'block', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}
            >
              {section.group}
            </Typography>
          )}
          <List sx={{ pt: section.group ? 0 : 2, pb: 0 }}>
            {section.items.map(renderItem)}
          </List>
          {i < MENU.length - 1 && <Divider sx={{ mx: 2, mt: 1 }} />}
        </Box>
      ))}
    </Box>
  );

  return (
    <SwipeableDrawer anchor="left" open={open} onClose={onClose} onOpen={() => {}}>
      {list()}
    </SwipeableDrawer>
  );
}
